import { useLazyQuery } from '@apollo/client/react';
import { isNotNullOrUndefined } from '@pointorama/shared-utils';
import { orderBy } from 'lodash/fp';
import React, { memo, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DeleteConfirmationProvider } from '../../contexts/DeleteConfirmationContext';
import { PotreeSourcesContext } from '../../contexts/PotreeSourcesContext';
import { RendererContext, RendererContextType } from '../../contexts/RendererContext';
import { RendererReadOnlyContext } from '../../contexts/RendererReadOnlyContext';
import { ProjectAccessLevelContext } from '../../contexts/ProjectAccessLevelContext';
import { setProjectWriteAccess } from '../../modules/Properties/reactive/projectWriteAccess';
import { UserContext } from '../../contexts/UserContext';
import { useUserDefaultColor, getUserMeasurementUnitProperty } from '../../modules/Properties/integration/UserSettingsIntegration';
import { getViewerEdlEnabledProperty } from '@pointorama/potree-shared';
import { REQUEST_READ_SAS_TOKEN } from '../../graphql/project';
import { useProject } from '../../hooks/potree/useProject';
import { useProjectShell } from '../../hooks/potree/useProjectShell';
import { EMPTY_ARRAY, ProjectionSystems } from '../../utils/constants';
import { setProjectId, setStorageContainer, setIsLocalProject, setStorageBaseUrl } from '../../potree/potreeState';
import { MeasurementUnits, ProjectAccessLevel, ProjectRequestReadSasTokenQuery, ProjectRequestReadSasTokenQueryVariables, UserRole } from '../../types/graphqlTypes';
import { CadLayer, CadObjectContext, InitialCadLayer } from '../../contexts/CadLayersContext';
import { usePreviewUpload } from '../../hooks/useUpload';
import { useViewManagerSideEffects } from '../../hooks/useViewManagerSideEffects';
import { ProjectContent } from '../../modules/project/ProjectContent';
import { useSyncCadLayers } from '../../hooks/modules/project/useSyncCadLayers';
import { useSyncProjectState } from '../../hooks/modules/project/useSyncProjectState';
import { useNavigationControls } from '../../hooks/useNavigationControls';
import { useProjectObjectLibrary } from '../../hooks/modules/objectLibrary/useProjectObjectLibrary';
import { AnnotationPropertyApplier } from '../../modules/Properties/integration/AnnotationPropertyIntegration';
import { AnnotationContextProvider } from '../../hooks/potree/useAnnotationContext';
import { CameraSettingsPropertyIntegration } from '../../modules/Properties/integration/CameraSettingsPropertyIntegration';
import { ProjectPropertyProvider } from '../../modules/Properties/hooks/ProjectPropertyContext';
import { loadPointCloud } from '../../potree/Potree';
import { PointSizeType } from '../../potree/defines';
import { Viewer } from '../../potree/viewer/viewer';
import { isShareMode } from '../../share/shareMode';

/** Renderless component that triggers the initial PROJECT fetch to populate Apollo cache.
 *  Isolated so its re-renders (on any project field change) don't cascade to Project_. */
const ProjectFetcher: React.FC = () => {
  useProject();
  return null;
};

/** Renderless barrier that hosts useSyncProjectState.
 *  This hook subscribes to annotation data via useQuery(PROJECT_ANNOTATION_STATE),
 *  so it must be isolated to prevent annotation changes from cascading through Project_. */
const SyncProjectStateBarrier = memo(({ projectProps }: { projectProps: Parameters<typeof useSyncProjectState>[0] }) => {
  useSyncProjectState(projectProps);
  return null;
});

const Project_: React.FC2 = () => {
  const { isLoaded: sourcesLoaded } = useContext(PotreeSourcesContext);
  const { projectId = '' } = useParams();
  const [setAndUploadPreview, uploadPreviewFile] = usePreviewUpload();
  const [projectRequestReadSASToken] = useLazyQuery<ProjectRequestReadSasTokenQuery, ProjectRequestReadSasTokenQueryVariables>(REQUEST_READ_SAS_TOKEN, { fetchPolicy: 'network-only' });
  const projectShell = useProjectShell(); // Render data — excludes annotations, won't re-render on annotation changes
  const currentUser = useContext(UserContext);
  const { organisationId = '' } = useParams();
  const project = projectShell;
  const initialProjectCadLayers = project?.state.cadLayers || EMPTY_ARRAY;
  const [cadLayers, setCadLayers] = useState<Record<string, CadLayer | InitialCadLayer | null>>({});
  const projectionSystem = project?.settings?.projectionSystem;
  // What this viewer may do here, as the server sees it (guests per project, members EDIT, share
  // visitors VIEW). Until the project has loaded, the role is the best guess: guests read-only.
  const isGuestRole = currentUser.rolesByOrganisation.find((value) => value.organisationId === organisationId)?.role === UserRole.Guest;
  const accessLevel: ProjectAccessLevel = isShareMode() ? ProjectAccessLevel.View : currentUser.isSuperAdmin ? ProjectAccessLevel.Edit : (project?.myAccessLevel ?? (isGuestRole ? ProjectAccessLevel.View : ProjectAccessLevel.Edit));
  // Anything below EDIT is viewing: no tools, no export, no preview uploads.
  const readOnly = accessLevel !== ProjectAccessLevel.Edit;

  useSyncCadLayers({ projectionSystem, initialProjectCadLayers, cadLayers, setCadLayers });

  // A one-shot latch guarding the async pointcloud load below. Nothing renders from it, and it
  // must flip synchronously (before the first await) so a re-entrant effect run cannot start a
  // duplicate load — that is a ref's job, not state's.
  const pointcloudLoadedRef = useRef(false);
  const [initializedPointclouds, setInitializedPointclouds] = useState<Array<string>>([]);
  const hasProject = !!project;
  const [rendererContext, setRendererContext] = React.useState<RendererContextType>({});
  // calculationsByIdentifier is now computed inside useAnnotationContext/useSyncProjectState
  // and no longer needs to flow through Project_

  // Initialize user color service (syncWindowColor runs internally via useEffect)
  useUserDefaultColor();
  const cadLayersArray = useMemo(() => Object.values(cadLayers).filter(isNotNullOrUndefined), [cadLayers]);

  // Hoist the nested slices so each memo closes over exactly the arrays it reads, rather than
  // the whole `project` object (which changes on every unrelated project field update).
  const wmsLayerOrderedIdentifiers = project?.state.wmsLayerOrderedIdentifiers;
  const wmsLayersRaw = project?.state.wmsLayers;
  const orthophotoLayerOrderedIdentifiers = project?.state.orthophotoLayerOrderedIdentifiers;
  const orthophotoLayersRaw = project?.state.orthophotoLayers;

  const wmsLayersContextValue = useMemo(() => {
    const orderedIdentifiers = orderBy('index', 'asc', wmsLayerOrderedIdentifiers);
    const wmsLayers = orderedIdentifiers.map(({ identifier }) => wmsLayersRaw?.find((layer) => layer.identifier === identifier)).filter(isNotNullOrUndefined);
    return { wmsLayers };
  }, [wmsLayerOrderedIdentifiers, wmsLayersRaw]);

  const orthophotoLayersContextValue = useMemo(() => {
    const orderedIdentifiers = orderBy('index', 'asc', orthophotoLayerOrderedIdentifiers);
    const orthophotoLayers = orderedIdentifiers.map(({ identifier }) => orthophotoLayersRaw?.find((layer) => layer.identifier === identifier)).filter(isNotNullOrUndefined);
    return { orthophotoLayers };
  }, [orthophotoLayerOrderedIdentifiers, orthophotoLayersRaw]);

  const syncProjectStateProps = useMemo(
    () => ({
      project,
      cadLayers,
      setCadLayers,
      initializedPointclouds,
      wmsLayersContextValue,
      orthophotoLayersContextValue,
      viewer: rendererContext.viewer,
      potreeSourcesLoaded: sourcesLoaded,
    }),
    [project, cadLayers, setCadLayers, initializedPointclouds, wmsLayersContextValue, orthophotoLayersContextValue, rendererContext.viewer, sourcesLoaded],
  );

  useEffect(() => {
    const viewer = rendererContext.viewer;
    if (!viewer) return;
    project?.pointClouds.forEach((pointCloud) => {
      const renderedPointCloud = viewer.scene.pointclouds.find((pc) => pc.identifier === pointCloud.id);
      if (renderedPointCloud) {
        renderedPointCloud.visible = pointCloud.visible;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.pointClouds]);

  const setPreview = useCallback(
    async ({ viewer }: { viewer: RendererContextType['viewer'] }) => {
      if (readOnly) return;
      // Skip preview generation when a superAdmin opens a project outside their own orgs
      const isMemberOfOrg = currentUser.rolesByOrganisation.some((v) => v.organisationId === organisationId);
      if (currentUser.isSuperAdmin && !isMemberOfOrg) return;

      // Skip if preview exists and is reachable
      if (project?.previewUrl) {
        try {
          const res = await fetch(project.previewUrl, { method: 'HEAD' });
          if (res.ok) return;
        } catch {
          // Preview URL is broken — regenerate below
        }
      }

      const result = viewer?.getRenderPreview();
      if (!result) return;

      try {
        // Upload primary (WebP) — this is what the DB points to
        const primaryExt = result.primary.type === 'image/webp' ? 'webp' : 'jpg';
        const primaryFile = new File([result.primary], `defaultrenderpreview.${primaryExt}`, { type: result.primary.type });
        await setAndUploadPreview(primaryFile, projectId);

        // Also upload a JPEG fallback alongside (no DB update, just a backup blob)
        if (result.primary.type !== result.fallback.type) {
          const fallbackFile = new File([result.fallback], 'defaultrenderpreview.jpg', { type: 'image/jpeg' });
          await uploadPreviewFile(fallbackFile, projectId);
        }
      } catch {
        // Upload may fail if storage container doesn't exist — silently ignore
      }
    },
    [setAndUploadPreview, uploadPreviewFile, projectId, project, currentUser.isSuperAdmin, currentUser.rolesByOrganisation, organisationId, readOnly],
  );

  useLayoutEffect(() => {
    if (!hasProject || !sourcesLoaded || pointcloudLoadedRef.current) return;
    if (!project.pointClouds) return;
    pointcloudLoadedRef.current = true;

    const loadPointCloudHeaderFiles = async ({ viewer }: { viewer: typeof window.viewer }) => {
      let SASUrl: Awaited<ReturnType<typeof projectRequestReadSASToken>>;
      try {
        SASUrl = await projectRequestReadSASToken({ variables: { projectId } });
      } catch (e) {
        // Apollo aborts the query when the page unmounts before the token arrives; the viewer is gone too.
        if (e instanceof Error && e.name === 'AbortError') return;
        throw e;
      }
      const SAStoken = SASUrl.data?.projectRequestReadSASToken || '';
      localStorage.setItem('SASToken_' + projectId, SAStoken);
      setProjectId(projectId);
      setStorageContainer(project.storageContainer);
      setIsLocalProject(project.isLocal || false);
      setStorageBaseUrl(project.storageBaseUrl || '');
      project.pointClouds.forEach((pointCloud) => {
        loadPointCloud(
          `/${project.storageContainer}/${pointCloud.cloudName}.ppch?${SAStoken}`,
          `${pointCloud.cloudName}`,
          (e) => {
            if (project.settings?.projectionSystem) {
              const projection = ProjectionSystems[project.settings.projectionSystem as keyof typeof ProjectionSystems].parameters;
              e.pointcloud.projection = projection;
            }
            viewer.scene.addPointCloud(e.pointcloud);
            const material = e.pointcloud.material;
            material.pointSizeType = PointSizeType.ADAPTIVE;

            viewer.fitToScreen();

            viewer.sourcesInfo.addSources({
              sources: pointCloud.availableSources,
              pointCloudId: pointCloud.id,
            });
            setInitializedPointclouds((pointclouds) => pointclouds.concat(pointCloud.id));
          },
          false,
          pointCloud.id,
          pointCloud.visible,
        );
      });
    };

    const initializeViewer = () => {
      // Default colors are now handled automatically by the property system

      const renderArea = document.getElementById('potree_render_area');
      if (!renderArea) {
        console.error('Could not find potree_render_area element');
        return;
      }
      const viewer = new Viewer(renderArea, { useDefaultRenderLoop: false });
      // Expose for debugging + the dispose path (viewer.ts reads window.viewer),
      // matching the standalone potree-renderer page.
      window.viewer = viewer;
      setRendererContext({ viewer });

      // Set background based on theme immediately after viewer creation
      // to ensure correct clear color from the first frame
      if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
        viewer.setBackground('dark');
      } else {
        viewer.setBackground('light');
      }

      viewer.setFOV(60);

      viewer.loadSettingsFromURL();
      viewer.loadGUI();
      loadPointCloudHeaderFiles({ viewer });
      setTimeout(() => {
        setPreview({ viewer });
      }, 10000);
    };

    initializeViewer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, projectRequestReadSASToken, sourcesLoaded, hasProject, project?.settings?.projectionSystem, project?.mapVisible, setInitializedPointclouds, project?.pointClouds]);

  // The viewer's tools gate their editing affordances (gizmos, drags) on this flag.
  useEffect(() => {
    const viewer = rendererContext.viewer;
    if (!viewer) return;
    viewer.setReadOnly(readOnly);
  }, [rendererContext.viewer, readOnly]);

  // Shared project state is only persisted by editors; a viewer's local changes stay local.
  useEffect(() => {
    setProjectWriteAccess(!readOnly);
    return () => setProjectWriteAccess(true);
  }, [readOnly]);

  // Cleanup viewer resources on unmount or page unload to prevent memory leaks
  useEffect(() => {
    const viewer = rendererContext.viewer;
    if (!viewer) return;

    const handleBeforeUnload = () => {
      viewer.dispose();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      viewer.dispose();
    };
  }, [rendererContext.viewer]);

  // Reactively sync measurement unit to Potree viewer
  useEffect(() => {
    const viewer = rendererContext.viewer;
    if (!viewer) return;

    const unitProperty = getUserMeasurementUnitProperty();
    const subscription = unitProperty.subscribe((unit) => {
      viewer.setLengthUnitAndDisplayUnit('m', unit === MeasurementUnits.Feet ? 'ft' : 'm');
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [rendererContext.viewer]);

  // Reactively sync EDL (point edges) toggle to the viewer (PROJECT_USER, viewer-scoped)
  useEffect(() => {
    const viewer = rendererContext.viewer;
    if (!viewer) return;

    const edlProperty = getViewerEdlEnabledProperty();
    const subscription = edlProperty.subscribe((enabled) => {
      viewer.setEDLEnabled(Boolean(enabled));
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [rendererContext.viewer]);

  // Listen for theme changes and update viewer background
  useEffect(() => {
    const viewer = rendererContext.viewer;
    if (!viewer) return;

    const updateBackground = () => {
      const currentBackground = viewer.getBackground();

      // Determine expected background based on current theme
      const expectedBackground = localStorage.theme === 'dark' ? 'dark' : 'light';

      // Only update if current background is already on a theme-matched color
      // This prevents changing the background when it's intentionally set to something else (e.g., black for xray)
      if (localStorage.theme && (currentBackground === 'dark' || currentBackground === 'light')) {
        viewer.setBackground(expectedBackground);
      }
    };

    // Listen for storage events (theme changes from other tabs)
    window.addEventListener('storage', updateBackground);

    // Listen for class changes on documentElement (theme changes in same tab)
    const observer = new MutationObserver(() => {
      updateBackground();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      window.removeEventListener('storage', updateBackground);
      observer.disconnect();
    };
  }, [rendererContext.viewer]);

  // Setup navigation controls for the viewer
  useNavigationControls({ viewer: rendererContext.viewer });
  useProjectObjectLibrary({ projectId, organisationId, readOnly });

  // View manager side-effects: init 2D mode from project, hide WMS/map on 3D switch
  useViewManagerSideEffects(rendererContext.viewer, project);

  const cadLayersContextValue = useMemo(() => {
    return { cadLayers: cadLayersArray.slice() };
  }, [cadLayersArray]);

  return (
    <RendererReadOnlyContext.Provider value={readOnly}>
      <ProjectAccessLevelContext.Provider value={accessLevel}>
      <ProjectFetcher />
      <ProjectPropertyProvider projectId={projectId}>
        <AnnotationContextProvider>
          <SyncProjectStateBarrier projectProps={syncProjectStateProps} />
          <RendererContext.Provider value={rendererContext}>
            <DeleteConfirmationProvider>
              <CadObjectContext.Provider value={cadLayersContextValue}>
                {/* Apply properties for annotations (boxes, planes, etc.) */}
                <AnnotationPropertyApplier projectId={projectId} />
                {/* Persist camera rotation settings */}
                <CameraSettingsPropertyIntegration />
                <ProjectContent project={project} />
              </CadObjectContext.Provider>
            </DeleteConfirmationProvider>
          </RendererContext.Provider>
        </AnnotationContextProvider>
      </ProjectPropertyProvider>
      </ProjectAccessLevelContext.Provider>
    </RendererReadOnlyContext.Provider>
  );
};

export const Project = memo(Project_);
